// PRD.315 — presentation helpers for patient age.
//
// The arithmetic and the DOB-vs-reported decision live in data-access
// (`resolvePatientAgeDisplay`); this module only turns that result into the
// strings the screens render, so every surface words provenance the same way.

import { resolvePatientAgeDisplay, type PatientAgeSource } from '@helios/data-access';

/** Repo-standard short date, matching the patient list formatters. */
export function formatReportedOn(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export interface PatientAgeView {
  /** Whole years, or null when nothing is known. */
  years: number | null;
  /** True when `years` is a stored observation rather than derived from DOB. */
  isReported: boolean;
  /**
   * Provenance line for a reported age, e.g. "Reported Aug 25, 2026". Empty for
   * a DOB-derived age (which needs no caveat) and for unknown.
   */
  provenance: string;
  /** Compact suffix for dense rows/tooltips, e.g. "35y (reported)". Empty when unknown. */
  compact: string;
}

/**
 * Resolve everything a demographic surface needs to render a patient's age.
 *
 * DOB-derived age is primary whenever a birth date exists. A reported age is
 * shown verbatim with the date it was reported, never aged forward — a record
 * that said 35 in 2026 must not quietly claim 45 in 2036 (PRD §9).
 */
export function toPatientAgeView(
  patient: PatientAgeSource | null | undefined,
  now: Date = new Date(),
): PatientAgeView {
  const resolved = resolvePatientAgeDisplay(patient, now);
  if (resolved.years === null) {
    return { years: null, isReported: false, provenance: '', compact: '' };
  }

  if (resolved.source === 'reported') {
    const on = formatReportedOn(resolved.reportedAt);
    return {
      years: resolved.years,
      isReported: true,
      provenance: on ? `Reported ${on}` : 'Reported age',
      compact: `${resolved.years}y (reported)`,
    };
  }

  return {
    years: resolved.years,
    isReported: false,
    provenance: '',
    compact: `${resolved.years}y`,
  };
}
